import { Box, Flex, Grid, HStack, Image, Spinner, Text, VStack, useDisclosure } from '@chakra-ui/react'
import { Button as UbiButton, Text as UbiText, Label, Column, Row, Card, Icon } from '@ubirider/pick-component-library'
import { useTranslation } from 'react-i18next'
import { useEffect, useState, useMemo } from 'react'
import { activatePickCard } from '../../../api/cards'
import { motion, AnimatePresence } from 'motion/react'

import EmptyState from '../../../assets/illustration_card_only.svg'
import PickLogo from '../../../assets/pick_logo.svg'
import E6Logo from '../../../assets/e6.svg'
import MastercardLogo from '../../../assets/mastercard_text.svg'
import PlusIcon from '../../../assets/plus_white.svg'
import ArrowLeft from '../../../assets/arrow_left_transfer.svg'
import ArrowRight from '../../../assets/arrow_right_transfer.svg'
import { DateTime } from 'luxon'
import { AddMoneyModal } from '../../../modals/AddMoneyModal'
import { useCards } from '../../../context/DataProvider'

const MBox = motion.create(Box)
const MFlex = motion.create(Flex)
const MColumn = motion.create(Column)
const MCard = motion.create(Card)
const MVStack = motion.create(VStack)
const MText = motion.create(Text)
const MUbiText = motion.create(UbiText)

export const PickCard = () => {
  const { t } = useTranslation()
  const { onOpen, isOpen, onClose } = useDisclosure()

  const { pickCard, loading: cardsLoading } = useCards()
  const loading = cardsLoading

  const [pickLoading, setPickLoading] = useState(false)
  const [optimisticActive, setOptimisticActive] = useState(false)

  const activateCard = async () => {
    try {
      if (!pickCard?.token) return
      setPickLoading(true)
      await new Promise(res => setTimeout(res, 1000))
      setOptimisticActive(true)
      await activatePickCard(pickCard.token)
    } catch (e) {
      setOptimisticActive(false)
      throw e
    } finally {
      setPickLoading(false)
    }
  }

  const hasCard = !!pickCard
  const isCardActive = optimisticActive || pickCard?.status === 'active'

  useEffect(() => {
    if (pickCard?.status === 'active' && optimisticActive) setOptimisticActive(false)
  }, [pickCard?.status, optimisticActive])

  useEffect(() => {
    if (!optimisticActive) return
    const id = setTimeout(() => setOptimisticActive(false), 15000)
    return () => clearTimeout(id)
  }, [optimisticActive])

  const variants = useMemo(
    () => ({
      container: {
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: { when: 'beforeChildren', staggerChildren: 0.08, delayChildren: 0.05 }
        },
        exit: { opacity: 0, transition: { duration: 0.15 } }
      },
      up: {
        hidden: { opacity: 0, y: 12 },
        visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 380, damping: 28 } },
        exit: { opacity: 0, y: 8, transition: { duration: 0.15 } }
      },
      scaleIn: {
        hidden: { opacity: 0, scale: 0.98 },
        visible: { opacity: 1, scale: 1, transition: { type: 'spring', stiffness: 320, damping: 24 } },
        exit: { opacity: 0, scale: 0.98, transition: { duration: 0.15 } }
      },
      listItem: {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 360, damping: 26 } }
      },
      buttonLate: {
        hidden: { opacity: 0, y: 8 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { type: 'spring', stiffness: 360, damping: 26, delay: 0.25 }
        }
      }
    }),
    []
  )

  console.log(pickCard?.transfers)

  return (
    <Flex direction='column' flex='1' gap='20px'>
      <MBox
        initial='hidden'
        animate='visible'
        exit='exit'
        variants={variants.container}
        bg={{ sm: '', md: 'neutral.100', lg: 'neutral.100', xl: 'neutral.100' }}
        rounded='8px'
        p={{ sm: '', md: '24px', lg: '24px', xl: '24px' }}
      >
        <AnimatePresence mode='wait'>
          {loading ? (
            <MFlex
              key='loading'
              minH='240px'
              align='center'
              justify='center'
              variants={variants.up}
              initial='hidden'
              animate='visible'
              exit='exit'
            >
              <Spinner thickness='6px' color='neutral.500' size='xl' />
            </MFlex>
          ) : hasCard ? (
            <MBox key='content' variants={variants.container}>
              <AnimatePresence mode='wait'>
                {isCardActive ? (
                  <MColumn key='active' css={{ flex: 1, gap: 24 }} variants={variants.up}>
                    <MText variants={variants.up} fontWeight='700' fontSize='24px'>
                      {t('demo.pick_card')}
                    </MText>

                    <Grid
                      templateColumns={{
                        sm: 'repeat(1, 1fr)',
                        md: 'repeat(2, 1fr)',
                        lg: 'repeat(2, 1fr)',
                        xl: 'repeat(2, 1fr)',
                        xxl: 'repeat(2, 1fr)'
                      }}
                      gap='24px'
                      overflowX='scroll'
                      w='100%'
                      sx={{ '::-webkit-scrollbar': { display: 'none' } }}
                    >
                      <MCard
                        variants={variants.scaleIn}
                        whileHover={{ y: -2 }}
                        whileTap={{ scale: 0.995 }}
                        css={{
                          height: '202px',
                          padding: 16,
                          borderRadius: 16,
                          border: '1px solid $neutral300',
                          background: 'linear-gradient(177deg, #9C2CF3 -13.49%, #3A49F9 109.75%)',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <Column css={{ gap: 16 }}>
                          <MUbiText variants={variants.up} type='textMD' weight='bold' css={{ color: 'white' }}>
                            {t('home.contactless_card')}
                          </MUbiText>
                          <Column css={{ gap: 4 }}>
                            <MUbiText variants={variants.up} type='textMD' css={{ color: 'white' }}>
                              {pickCard.number.match(/.{1,4}/g).join(' ')}
                            </MUbiText>
                            <MUbiText variants={variants.up} type='textMD' css={{ color: 'white' }}>
                              {`${pickCard.expiration.substring(0, 2)}/${pickCard.expiration.substring(2)}`}
                            </MUbiText>
                          </Column>
                        </Column>
                        <Row css={{ justifyContent: 'space-between', alignItems: 'flex-end', flex: 1 }}>
                          <Label type='neutralLight' style='outline' label={t('home.active')} />
                          <Row css={{ gap: 8 }}>
                            <Image src={PickLogo} height='24px' />
                            <Image src={MastercardLogo} height='24px' />
                            <Image src={E6Logo} height='24px' />
                          </Row>
                        </Row>
                      </MCard>

                      <MColumn css={{ flex: 1 }} variants={variants.up}>
                        <MUbiText variants={variants.up} type='textLG'>
                          {t('demo.current_balance')}
                        </MUbiText>
                        <MUbiText variants={variants.up} type='displayMD' weight='bold'>
                          {pickCard?.balance?.toFixed(2) ?? '0.00'}€
                        </MUbiText>

                        <Column css={{ flex: 1, justifyContent: 'flex-end', mb: 16 }}>
                          <motion.div variants={variants.buttonLate}>
                            <UbiButton
                              type='neutralLight'
                              style='solid'
                              size='small'
                              label={t('demo.add_money')}
                              leftIconProps={{ src: PlusIcon }}
                              onClick={onOpen}
                              css={{ width: 'fit-content' }}
                            />
                          </motion.div>
                        </Column>
                      </MColumn>
                    </Grid>
                  </MColumn>
                ) : (
                  <MColumn
                    key='inactive'
                    css={{ gap: '24px', flex: '1' }}
                    variants={variants.scaleIn}
                    initial='hidden'
                    animate='visible'
                    exit='exit'
                  >
                    <MText variants={variants.up} fontWeight='700' fontSize='24px'>
                      {t('demo.pick_card')}
                    </MText>
                    <MColumn
                      variants={variants.container}
                      css={{ gap: '24px', px: '48px', justifyContent: 'center', alignItems: 'center' }}
                    >
                      <MCard
                        variants={variants.scaleIn}
                        whileHover={{ y: -1 }}
                        css={{
                          width: 250,
                          height: 156,
                          padding: '16px 16px 24px 16px',
                          borderRadius: 16,
                          border: '1px solid $neutral300',
                          background: 'linear-gradient(177deg, #9C2CF3 -13.49%, #3A49F9 109.75%)',
                          display: 'flex',
                          flexDirection: 'column'
                        }}
                      >
                        <Row css={{ justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                          <Image src={PickLogo} height='16px' />
                          <Image src={MastercardLogo} height='24px' />
                          <Image src={E6Logo} height='26px' />
                        </Row>

                        <Row css={{ alignItems: 'flex-end', flex: 1 }}>
                          <MUbiText variants={variants.up} type='textSM' css={{ color: 'white', marginTop: 'auto' }}>
                            **** **** ****
                          </MUbiText>
                        </Row>
                      </MCard>
                      <MUbiText variants={variants.up} type='textSM' css={{ textAlign: 'center' }}>
                        {t('demo.pick_card_description')}
                      </MUbiText>
                      <motion.div variants={variants.buttonLate}>
                        <UbiButton
                          style='solid'
                          type='primary'
                          label={t('demo.get_card_for_free')}
                          loading={pickLoading}
                          onClick={activateCard}
                        />
                      </motion.div>
                    </MColumn>
                  </MColumn>
                )}
              </AnimatePresence>
            </MBox>
          ) : (
            <MColumn
              key='no-card'
              css={{ gap: 24, flex: 1, alignItems: 'center', justifyContent: 'center', px: 48, py: 24 }}
              initial='hidden'
              animate='visible'
              exit='exit'
              variants={variants.container}
            >
              <MText variants={variants.up} fontWeight='700' fontSize='24px'>
                {t('demo.pick_card')}
              </MText>

              <div style={{ position: 'relative', width: 220, height: 160 }}>
                <motion.div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 16,
                    border: '1px solid rgba(0,0,0,0.06)',
                    background: 'linear-gradient(177deg, #D7D7D7 -13.49%, #F0F0F0 109.75%)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
                  }}
                  initial={{ opacity: 0, y: 18, rotate: -6, scale: 0.96 }}
                  animate={{ opacity: 0.7, y: [18, 10, 18], rotate: -6, scale: 0.96 }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    transformOrigin: 'center',
                    borderRadius: 16,
                    border: '1px solid rgba(0,0,0,0.06)',
                    background: 'linear-gradient(177deg, #9C2CF3 -13.49%, #3A49F9 109.75%)',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 16
                  }}
                  initial={{ opacity: 0, y: 12, rotate: 0, scale: 0.98 }}
                  animate={{ opacity: 1, y: [12, 4, 12], rotate: 0, scale: 1 }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <Row css={{ justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                    <Image src={PickLogo} height='14px' />
                    <Image src={MastercardLogo} height='22px' />
                  </Row>
                  <Row css={{ alignItems: 'flex-end', flex: 1 }}>
                    <MUbiText variants={variants.up} type='textSM' css={{ color: 'white', marginTop: 'auto' }}>
                      **** **** ****
                    </MUbiText>
                  </Row>
                </motion.div>
              </div>

              <MUbiText variants={variants.up} type='textSM' css={{ textAlign: 'center', maxWidth: 560 }}>
                {t('demo.pick_card_description')}
              </MUbiText>

              <motion.div variants={variants.buttonLate}>
                <UbiButton
                  style='solid'
                  type='primary'
                  label={t('demo.get_card_for_free')}
                  onClick={activateCard}
                  loading={pickLoading}
                />
              </motion.div>
            </MColumn>
          )}
        </AnimatePresence>
      </MBox>

      <AnimatePresence>
        {isCardActive && (
          <MBox
            key='history'
            initial='hidden'
            animate='visible'
            exit='exit'
            variants={variants.container}
            bg={{ sm: '', md: 'neutral.100', lg: 'neutral.100', xl: 'neutral.100' }}
            rounded='8px'
            p={{ sm: '', md: '24px', lg: '24px', xl: '24px' }}
          >
            <MVStack align='stretch' spacing='24px' variants={variants.up}>
              <MText variants={variants.up} fontWeight='700' fontSize='24px'>
                {t('demo.topups_transfers')}
              </MText>

              {!pickCard.transfers || pickCard?.transfers?.length === 0 ? (
                <MFlex
                  key='empty'
                  direction='column'
                  gap='24px'
                  align='center'
                  flex='1'
                  px='48px'
                  variants={variants.up}
                >
                  <Image src={EmptyState} w='262px' />
                  <MText variants={variants.up} fontSize='14px' textAlign='center'>
                    {t('demo.topups_transfers_description')}
                  </MText>
                  <motion.div variants={variants.buttonLate}>
                    <UbiButton
                      type='primary'
                      style='solid'
                      label={t('demo.add_money')}
                      leftIconProps={{ src: PlusIcon }}
                      onClick={onOpen}
                      css={{ width: 'fit-content' }}
                    />
                  </motion.div>
                </MFlex>
              ) : (
                <MBox
                  key='list'
                  initial='hidden'
                  animate='visible'
                  exit='exit'
                  variants={variants.container}
                  as='div'
                  p='8px'
                  borderRadius='16px'
                  border='1px solid'
                  borderColor='neutral.200'
                  bg='white'
                >
                  {pickCard.transfers.map((transfer, index) => (
                    <motion.div
                      key={transfer.id ?? `${transfer.type}-${transfer.date}-${index}`}
                      variants={variants.listItem}
                      style={{ borderRadius: 12 }}
                    >
                      <HStack
                        px='16px'
                        py='12px'
                        borderBottom={index < pickCard.transfers.length - 1 ? '1px solid' : '0'}
                        borderColor='neutral.200'
                        justify='space-between'
                        align='center'
                        _hover={{ bg: 'blackAlpha.50' }}
                      >
                        <HStack spacing='16px'>
                          <Icon src={transfer.type === 2 ? ArrowRight : ArrowLeft} />
                          <Column>
                            <UbiText type='textSM' weight='bold'>
                              {transfer.type === 1
                                ? t('demo.money_added', { cardNumber: transfer.bank ? t('demo.bank_account').toLowerCase() : transfer.cardNumber.slice(-6) })
                                : transfer.type === 2
                                  ? t('demo.money_transferred', { cardNumber: transfer.bank ? t('demo.bank_account') : transfer.cardNumber.slice(-6) })
                                  : t('demo.cashback')}
                            </UbiText>
                            <UbiText type='textSM' weight='semibold' css={{ color: '$neutral600' }}>
                              {DateTime.fromMillis(transfer.date).toFormat('dd/LL/yy, HH:mm')}
                            </UbiText>
                          </Column>
                        </HStack>

                        <UbiText
                          type='textSM'
                          weight='semibold'
                          css={{ color: transfer.type === 2 ? '$neutral600' : '$neutral700' }}
                        >
                          {transfer.type === 2 ? '-' : '+'}{transfer?.amount?.toFixed(2)}€
                        </UbiText>
                      </HStack>
                    </motion.div>
                  ))}
                </MBox>
              )}
            </MVStack>
          </MBox>
        )}
      </AnimatePresence>

      <AddMoneyModal pickCard={pickCard} onOpen={onOpen} isOpen={isOpen} onClose={onClose} />
    </Flex>
  )
}